"use client"

import Link from "next/link";
import { Mail, MapPin, Phone } from "lucide-react";

import LeadForm from "@/components/forms/LeadForm";
import { type Locale } from "@/i18n-config";

interface ContactHeroDict {
    common?: {
        home?: string;
    };
    contact_page?: {
        title?: string;
        intro?: string;
        appointment_label?: string;
        patient_feedback_label?: string;
        employee_feedback_label?: string;
        editor_label?: string;
        editor_name?: string;
        editor_date_label?: string;
        editor_date?: string;
        address_label?: string;
        address_line_1?: string;
        address_line_2?: string;
        form_title?: string;
        form_intro?: string;
    };
    form?: {
        send?: string;
    };
}

export default function ContactHero({ dict, lang = "tr" }: { dict?: ContactHeroDict; lang?: Locale }) {
    const contactItems = [
        {
            icon: Phone,
            label: dict?.contact_page?.appointment_label || "Özel Cinik Polikliniği Randevu Hattı",
            className: "",
            content: (
                <a href="tel:+905412340044" className="transition-colors hover:text-white">
                    +90 541 234 00 44
                </a>
            ),
        },
        {
            icon: Mail,
            label: dict?.contact_page?.patient_feedback_label || "Hasta ve Hasta Yakını Görüş ve Önerileri için",
            className: "",
            content: (
                <a href="mailto:info@cinikpoliklinigi.com" className="transition-colors hover:text-white">
                    info@cinikpoliklinigi.com
                </a>
            ),
        },
        {
            icon: Mail,
            label: dict?.contact_page?.employee_feedback_label || "Çalışan Görüş ve Önerileri için",
            className: "",
            content: (
                <a href="mailto:info@cinikpoliklinigi.com" className="transition-colors hover:text-white">
                    info@cinikpoliklinigi.com
                </a>
            ),
        },
        {
            icon: Phone,
            label: dict?.contact_page?.editor_label || "Editör",
            className: "",
            content: (
                <div className="space-y-1">
                    <div>
                        {dict?.contact_page?.editor_name || "Burcu Yiğit Tekin"}:{" "}
                        <a href="tel:+905496482511" className="transition-colors hover:text-white">
                            +90 541 234 00 44
                        </a>
                    </div>
                    <div className="text-[13px] font-medium text-[#8faecb]">
                        {(dict?.contact_page?.editor_date_label || "Güncelleme tarihi")}{" "}
                        {dict?.contact_page?.editor_date || "14.01.2026"}
                    </div>
                </div>
            ),
        },
        {
            icon: MapPin,
            label: dict?.contact_page?.address_label || "Adres",
            className: "md:col-span-2",
            content: (
                <div>
                    {dict?.contact_page?.address_line_1 || "Dikilitaş Mah. Ayazmaderesi Cad. No:4 Kat:1 34349"}
                    <br />
                    {dict?.contact_page?.address_line_2 || "Beşiktaş / İstanbul"}
                </div>
            ),
        },
    ];

    return (
        <section className="bg-[#051e3c] py-10 pb-20 lg:py-12 lg:pb-24">
            <div className="container-custom">
                <div className="mb-12 flex items-center text-[13px] font-medium text-[#8faecb]">
                    <Link href={lang === "tr" ? "/" : `/${lang}`} className="transition-colors hover:text-white">
                        {dict?.common?.home}
                    </Link>
                    <span className="mx-3 text-[10px] opacity-60">•</span>
                    <span className="text-gray-400">{dict?.contact_page?.title}</span>
                </div>

                <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(360px,460px)] lg:items-start lg:gap-12">
                    <div className="min-w-0">
                        <div className="max-w-[640px]">
                            <h1 className="mb-5 text-4xl font-bold tracking-tight text-white md:text-[42px]">
                                {dict?.contact_page?.title}
                            </h1>
                            <p className="text-[16px] leading-[1.7] text-gray-300/80">
                                {dict?.contact_page?.intro}
                            </p>
                        </div>

                        <div className="mt-10 grid gap-4 md:grid-cols-2">
                            {contactItems.map((item) => {
                                const Icon = item.icon;

                                return (
                                    <div
                                        key={item.label}
                                        className={`flex h-full items-start gap-4 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 ${item.className}`}
                                    >
                                        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white">
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <div className="mb-1 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#8faecb]">
                                                {item.label}
                                            </div>
                                            <div className="text-[16px] font-medium leading-7 text-white/95">
                                                {item.content}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="w-full rounded-2xl bg-white p-6 shadow-2xl lg:p-8">
                        <div className="mb-6 border-b border-slate-200 pb-5">
                            <h2 className="text-[28px] font-bold tracking-tight text-[#102a43]">
                                {dict?.contact_page?.form_title || "Bize Yazın"}
                            </h2>
                            <p className="mt-2 text-[15px] leading-7 text-slate-500">
                                {dict?.contact_page?.form_intro || "Bilgilerinizi bırakın, ekibimiz size en kısa sürede dönüş sağlasın."}
                            </p>
                        </div>

                        <LeadForm dict={dict} submitLabel={dict?.form?.send} theme="light" idPrefix="contact-hero-form" lang={lang} />
                    </div>
                </div>
            </div>
        </section>
    );
}
